import TypographyParagraph from '../../typography/TypographyParagraph'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { useRef } from 'react'
import {
	capitalizeFirstLetter,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import { CollectionInputDataTypes } from '#app/components/inputs/CollectionInputs/collectionNFTsInput'
import clsx from 'clsx'
import { CollectionNFTInputTypeNameMap } from '#app/utils/templates/utils/templateTypes'

export default function CollectionTemplate({
	collection,
}: {
	collection: any
}) {
	const tooltipCopyCollectionName = useRef<TooltipRefProps>(null)
	const tooltipCopyContractAddress = useRef<TooltipRefProps>(null)
	const tooltipCopyChain = useRef<TooltipRefProps>(null)
	const tooltipCopyGroupNFTsCID = useRef<TooltipRefProps>(null)
	const tooltipCopyTokenStart = useRef<TooltipRefProps>(null)
	const tooltipCopyTokenEnd = useRef<TooltipRefProps>(null)
	const tooltipCopyTokenIDs = useRef<TooltipRefProps>(null)
	const tooltipCopyDate = useRef<TooltipRefProps>(null)
	const tooltipCopyCreatorAddress = useRef<TooltipRefProps>(null)
	const tooltipCopyExternalLink = useRef<TooltipRefProps>(null)
	const tooltipCopySignature = useRef<TooltipRefProps>(null)

	return (
		<>
			<div
				id="collectionName"
				className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(collection.properties.name)
					tooltipCopyCollectionName.current?.open({
						anchorSelect: '#collectionName',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyCollectionName.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyCollectionName} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Collection Name:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{collection.properties.name}
				</TypographyParagraph>
			</div>
			<div
				id="groupNFTsCID"
				className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(collection.properties.GroupNFTsCID)
					tooltipCopyGroupNFTsCID.current?.open({
						anchorSelect: '#groupNFTsCID',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyGroupNFTsCID.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyGroupNFTsCID} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Group NFTs CID:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{collection.properties.GroupNFTsCID}
				</TypographyParagraph>
			</div>
			{collection.properties.nfts.map(
				(nftEntry: CollectionInputDataTypes, index: number) => {
					if (nftEntry.type === 'smartContractRange') {
						return (
							<div key={`${nftEntry.type}-${index}`}>
								<div className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark">
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Entry Type:
									</TypographyParagraph>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									>
										{CollectionNFTInputTypeNameMap[nftEntry.type]}
									</TypographyParagraph>
								</div>
								<div className="flex flex-col sm:flex-row">
									<div
										id={`contractAddress-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.contract)
											tooltipCopyContractAddress.current?.open({
												anchorSelect: `#contractAddress-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyContractAddress.current?.close()
											}, 2000)
										}}
									>
										<Tooltip
											imperativeModeOnly
											ref={tooltipCopyContractAddress}
										/>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Contract Address:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{truncateStringInMiddle(nftEntry.contract)}
										</TypographyParagraph>
									</div>
									<div
										id={`chain-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.chain)
											tooltipCopyChain.current?.open({
												anchorSelect: `chain-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyChain.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyChain} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Chain:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{capitalizeFirstLetter(nftEntry.chain)}
										</TypographyParagraph>
									</div>
								</div>
								<div className="flex flex-col sm:flex-row">
									<div
										id={`tokenStart-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.tokenStart)
											tooltipCopyTokenStart.current?.open({
												anchorSelect: `#tokenStart-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyTokenStart.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyTokenStart} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Token Start:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{truncateStringInMiddle(nftEntry.tokenStart)}
										</TypographyParagraph>
									</div>
									<div
										id={`tokenEnd-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.tokenEnd)
											tooltipCopyTokenEnd.current?.open({
												anchorSelect: `tokenEnd-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyTokenEnd.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyTokenEnd} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Token End:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{truncateStringInMiddle(nftEntry.tokenEnd)}
										</TypographyParagraph>
									</div>
								</div>
							</div>
						)
					} else if (nftEntry.type === 'smartContractIDs') {
						return (
							<div key={`${nftEntry.type}-${index}`}>
								<div className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark">
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Entry Type:
									</TypographyParagraph>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									>
										{CollectionNFTInputTypeNameMap[nftEntry.type]}
									</TypographyParagraph>
								</div>
								<div className="flex flex-col sm:flex-row">
									<div
										id={`contractAddress-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.contract)
											tooltipCopyContractAddress.current?.open({
												anchorSelect: `#contractAddress-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyContractAddress.current?.close()
											}, 2000)
										}}
									>
										<Tooltip
											imperativeModeOnly
											ref={tooltipCopyContractAddress}
										/>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Contract Address:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{truncateStringInMiddle(nftEntry.contract)}
										</TypographyParagraph>
									</div>
									<div
										id={`chain-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.chain)
											tooltipCopyChain.current?.open({
												anchorSelect: `chain-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyChain.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyChain} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Chain:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{capitalizeFirstLetter(nftEntry.chain)}
										</TypographyParagraph>
									</div>
								</div>
								<div className="flex flex-col sm:flex-row">
									<div
										id={`tokenIDs-${index}`}
										className="flex w-full flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
										onClick={() => {
											navigator.clipboard.writeText(
												nftEntry.tokenIDs.join(', '),
											)
											tooltipCopyTokenIDs.current?.open({
												anchorSelect: `#tokenIDs-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyTokenIDs.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyTokenIDs} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Token IDs:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{nftEntry.tokenIDs
												.map(tokenID => truncateStringInMiddle(tokenID))
												.join(', ')}
										</TypographyParagraph>
									</div>
								</div>
							</div>
						)
					} else if (nftEntry.type === 'smartContractFull') {
						return (
							<div key={`${nftEntry.type}-${index}`}>
								<div className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark">
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Entry Type:
									</TypographyParagraph>
									<TypographyParagraph
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									>
										{CollectionNFTInputTypeNameMap[nftEntry.type]}
									</TypographyParagraph>
								</div>
								<div className="flex flex-col sm:flex-row">
									<div
										id={`contractAddress-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.contract)
											tooltipCopyContractAddress.current?.open({
												anchorSelect: `#contractAddress-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyContractAddress.current?.close()
											}, 2000)
										}}
									>
										<Tooltip
											imperativeModeOnly
											ref={tooltipCopyContractAddress}
										/>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Contract Address:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{truncateStringInMiddle(nftEntry.contract)}
										</TypographyParagraph>
									</div>
									<div
										id={`chain-${index}`}
										className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
										onClick={() => {
											navigator.clipboard.writeText(nftEntry.chain)
											tooltipCopyChain.current?.open({
												anchorSelect: `chain-${index}`,
												content: 'Copied!',
											})
											setTimeout(() => {
												tooltipCopyChain.current?.close()
											}, 2000)
										}}
									>
										<Tooltip imperativeModeOnly ref={tooltipCopyChain} />
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
										>
											Chain:
										</TypographyParagraph>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											{capitalizeFirstLetter(nftEntry.chain)}
										</TypographyParagraph>
									</div>
								</div>
							</div>
						)
					} else {
						return null
					}
				},
			)}
			<div className="flex flex-col sm:flex-row">
				{collection.properties.date && collection.properties.date !== '' && (
					<div
						id="date"
						className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
						onClick={() => {
							navigator.clipboard.writeText(collection.properties.date)
							tooltipCopyDate.current?.open({
								anchorSelect: '#date',
								content: 'Copied!',
							})
							setTimeout(() => {
								tooltipCopyDate.current?.close()
							}, 2000)
						}}
					>
						<Tooltip imperativeModeOnly ref={tooltipCopyDate} />
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Date:
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{collection.properties.date}
						</TypographyParagraph>
					</div>
				)}
				<div
					id="creatorAddress"
					className={clsx(
						'flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark',
						collection.properties.date ? 'sm:w-1/2' : 'w-full',
					)}
					onClick={() => {
						navigator.clipboard.writeText(collection.address)
						tooltipCopyCreatorAddress.current?.open({
							anchorSelect: '#creatorAddress',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyCreatorAddress.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyCreatorAddress} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Creator Address:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{truncateStringInMiddle(collection.address)}
					</TypographyParagraph>
				</div>
			</div>
			{collection.properties.url && (
				<div
					id="url"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
					onClick={() => {
						navigator.clipboard.writeText(collection.properties.url)
						tooltipCopyExternalLink.current?.open({
							anchorSelect: '#url',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyExternalLink.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyExternalLink} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						External Link:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{collection.properties.url}
					</TypographyParagraph>
				</div>
			)}
			<div
				id="signature"
				className="flex flex-row p-4 hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(collection.signature)
					tooltipCopySignature.current?.open({
						anchorSelect: '#signature',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopySignature.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopySignature} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Signature:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{collection.signature}
				</TypographyParagraph>
			</div>
		</>
	)
}
